import * as THREE from "three";

const WALK_SPEED = 5.2;
const RUN_SPEED = 8.4;
const ACCEL = 12;
const JUMP_SPEED = 8;
const EYE_HEIGHT = 1.15;
const SPHERE_RADIUS = 0.45;

class PlayerController {
  constructor({ camera, body, collidables, sensitivity = 0.0022 }) {
    this.camera = camera;
    this.body = body;
    this.collidables = collidables;
    this.sensitivity = sensitivity;
    this.enabled = false;
    this.yaw = 0;
    this.pitch = 0;
    this.keys = new Set();
    this.grounded = false;
    this.lastGroundedAt = -1;
    this.lastJumpAt = -1;
    this.bobPhase = 0;
    this.horizontalVelocity = new THREE.Vector3();
    this.downRay = new THREE.Raycaster();
    this.downRay.far = SPHERE_RADIUS + 0.35;
    this.down = new THREE.Vector3(0, -1, 0);
    this.forward = new THREE.Vector3();
    this.right = new THREE.Vector3();
    this.wish = new THREE.Vector3();

    this.onKeyDown = (event) => {
      if (!this.enabled) return;
      if (event.code === "Space") event.preventDefault();
      this.keys.add(event.code);
    };
    this.onKeyUp = (event) => {
      this.keys.delete(event.code);
    };
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  handleMouseMove(dx, dy) {
    if (!this.enabled) return;
    this.yaw -= dx * this.sensitivity;
    this.pitch -= dy * this.sensitivity;
    const limit = Math.PI / 2 - 0.04;
    this.pitch = Math.max(-limit, Math.min(limit, this.pitch));
  }

  applyRecoil() {
    this.pitch += 0.007;
    this.yaw += (Math.random() - 0.5) * 0.004;
    const limit = Math.PI / 2 - 0.04;
    this.pitch = Math.min(limit, this.pitch);
  }

  checkGrounded() {
    const origin = new THREE.Vector3(
      this.body.position.x,
      this.body.position.y + 0.2,
      this.body.position.z
    );
    this.downRay.set(origin, this.down);
    const hits = this.downRay.intersectObjects(this.collidables, false);
    this.grounded = hits.length > 0;
    if (this.grounded) this.lastGroundedAt = performance.now() / 1000;
  }

  update(dt, elapsed) {
    this.checkGrounded();
    const keys = this.keys;

    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this.wish.set(0, 0, 0);
    if (keys.has("KeyW") || keys.has("ArrowUp")) this.wish.add(this.forward);
    if (keys.has("KeyS") || keys.has("ArrowDown")) this.wish.sub(this.forward);
    if (keys.has("KeyD") || keys.has("ArrowRight")) this.wish.add(this.right);
    if (keys.has("KeyA") || keys.has("ArrowLeft")) this.wish.sub(this.right);

    const moving = this.wish.lengthSq() > 0;
    if (moving) this.wish.normalize();
    const sprinting = (keys.has("ShiftLeft") || keys.has("ShiftRight")) && moving;
    const speed = sprinting ? RUN_SPEED : WALK_SPEED;
    this.wish.multiplyScalar(speed);

    const blend = 1 - Math.exp(-ACCEL * dt);
    this.horizontalVelocity.lerp(this.wish, blend);
    this.body.velocity.x = this.horizontalVelocity.x;
    this.body.velocity.z = this.horizontalVelocity.z;
    if (moving) this.body.wakeUp();

    const now = performance.now() / 1000;
    const coyoteOk = now - this.lastGroundedAt < 0.12;
    if (keys.has("Space") && coyoteOk && now - this.lastJumpAt > 0.3) {
      this.body.velocity.y = JUMP_SPEED;
      this.body.wakeUp();
      this.lastJumpAt = now;
      this.lastGroundedAt = -1;
    }

    const speedRatio = Math.min(
      this.horizontalVelocity.length() / RUN_SPEED,
      1
    );
    if (this.grounded && speedRatio > 0.05) {
      this.bobPhase += dt * (6 + speedRatio * 6);
    }
    const bob = Math.sin(this.bobPhase) * 0.035 * speedRatio;

    this.camera.position.set(
      this.body.position.x,
      this.body.position.y + EYE_HEIGHT + bob,
      this.body.position.z
    );
    this.camera.quaternion.setFromEuler(
      new THREE.Euler(this.pitch, this.yaw, 0, "YXZ")
    );
  }

  lookAtFrom(position) {
    this.body.position.set(position.x, position.y + SPHERE_RADIUS, position.z);
    this.body.velocity.set(0, 0, 0);
    this.horizontalVelocity.set(0, 0, 0);
    const toCenter = new THREE.Vector3(
      -position.x,
      0,
      -position.z
    ).normalize();
    this.yaw = Math.atan2(-toCenter.x, -toCenter.z);
    this.pitch = 0;
  }

  get eyePosition() {
    return new THREE.Vector3(
      this.body.position.x,
      this.body.position.y + EYE_HEIGHT,
      this.body.position.z
    );
  }

  get chestPosition() {
    return new THREE.Vector3(
      this.body.position.x,
      this.body.position.y + 0.55,
      this.body.position.z
    );
  }

  dispose() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.keys.clear();
  }
}

export { PlayerController, SPHERE_RADIUS };
export default PlayerController;
